import { i18n } from '@/i18n'
import { 
  GtmEventBase, 
  GtmEventType, 
  gtmHelper, 
  gtmSender 
} from '@/services/gtm'
import { 
  ContactInfo, 
  ContactType, 
  PackagedElectricityMeter,
  IndividualVatInfoSelection,
  FreelanceVatInfoSelection, 
  CompanyVatInfoFirstStepSelection,
  CompanyVatInfoSecondStepSelection,
  VatInfo
} from '@/models'
import { BuildingType } from '@/models/building-type'
import { CmhAddressPoint } from '@/models/cmh-address-point'
import { FileInfo } from '@ores/vue-library';

export interface NewConnectionCmhGtmFormData {  
  eventType: GtmEventType
  step: number 
  language: string
  ctaName?: string | null
  linkUrl?: string | null
  addressPoint?: CmhAddressPoint | null
  contactInfo?: ContactInfo | null
  offeringSelections?: string[] | null 
  packages?: PackagedElectricityMeter[] | null
  initialPackages?: PackagedElectricityMeter[] | null
  files?: FileInfo[] | null
  vatInfo?: VatInfo | null
  totalPrice?: number | null
}

class NewConnectionCmhGtmEvent extends GtmEventBase {
  building_type: string | null = null
  type_energy: string | null = null
  operators: string | null = null
  nb_appart: string | null = null
  nb_common_counter: string | null = null
  forfait: string | null = null
  forfait_status: string | null = null
  user_profil: string | null = null
  extra: string | null = null
  tva_applicable: string | null = null
  estimated_cost: string | null = null
  puissance_electrique_max: string | null = null
  cta_name: string | null = null
  link_url: string | null = null
} 

const steps: { [key: number]: string } = {
  [-1]: 'nouveau_raccordement|landing_page',
  [0]: 'form_start',
  [1]: 'nouveau _raccordement|adresse',
  [2]: 'nouveau_raccordement|demande', 
  [3]: 'nouveau_raccordement|forfait', 
  [4]: 'nouveau_raccordement|production', 
  [5]: 'nouveau_raccordement|contact',
  [6]: 'nouveau_raccordement|tva', 
  [7]: 'nouveau_raccordement|extra', 
  [8]: 'nouveau_raccordement|resume', 
  [9]: 'form_submit' 
}

const userProfiles: { [key in ContactType]: string } = {
  ['individual'] : "particulier",
  ['freelance'] : "indépendant",
  ['company']:  "société"
}

const buildingTypes: { [key in BuildingType]: string } = {
  [BuildingType.building]: 'immeuble',
  [BuildingType.house]: 'maison_unifamiliale',
  [BuildingType.unknown]: 'inconnu'
}

const individualVatInfoDescriptions: { [key in IndividualVatInfoSelection]: string } = {
  [IndividualVatInfoSelection.LessThanTenYears]: 'vatInfo.lessThanTenYearsDescriptionNormalized',
  [IndividualVatInfoSelection.MoreThanTenYears]: 'vatInfo.moreThanTenYearsDescriptionNormalized',
}

const freelanceVatInfoDescriptions: { [key in FreelanceVatInfoSelection]: string } = {
  [FreelanceVatInfoSelection.WithDeclaration]: 'vatInfo.withDeclarationDescription',
  [FreelanceVatInfoSelection.WithoutDeclaration]: 'vatInfo.withoutDeclarationDescription',
  [FreelanceVatInfoSelection.NotSubject]: 'vatInfo.notSubjectDescription'
}

const companyPrimaryVatInfoDescriptions: { [key in CompanyVatInfoFirstStepSelection]: string } = {
  [CompanyVatInfoFirstStepSelection.SubjectToVat]: 'vatInfo.subjectToVatDescription',
  [CompanyVatInfoFirstStepSelection.TownWithoutSocialCharacter]: 'vatInfo.townWithoutSocialCharacterDescription',
  [CompanyVatInfoFirstStepSelection.SocialHousing]: 'vatInfo.socialHousingDescription',
  [CompanyVatInfoFirstStepSelection.Embassy]: 'vatInfo.embassyDescription',
  [CompanyVatInfoFirstStepSelection.TownWithSocialCharacter]: 'vatInfo.townWithSocialCharacterDescription',
  [CompanyVatInfoFirstStepSelection.HousingForDisabled]: 'vatInfo.housingForDisabledDescription',
  [CompanyVatInfoFirstStepSelection.RestHouse]: 'vatInfo.restHouseDescription',
  [CompanyVatInfoFirstStepSelection.School]: 'vatInfo.schoolDescription'
}

const companySecondaryVatInfoDescriptions: { [key in CompanyVatInfoSecondStepSelection]: string } = {
    [CompanyVatInfoSecondStepSelection.WithDeclaration]: 'vatInfo.withDeclarationDescription',
    [CompanyVatInfoSecondStepSelection.WithoutDeclaration]: 'vatInfo.withoutDeclarationDescription',
    [CompanyVatInfoSecondStepSelection.PublicBuilding]: 'vatInfo.publicBuildingDescription',      
    [CompanyVatInfoSecondStepSelection.PrivateBuildingMoreThanTenYears]: 'vatInfo.privateBuildingMoreThanTenYearsDescription',
    [CompanyVatInfoSecondStepSelection.PrivateBuildingLessThanTenYears]: 'vatInfo.privateBuildingLessThanTenYearsDescription',
    [CompanyVatInfoSecondStepSelection.SocialPrivateBuilding]: 'vatInfo.socialPrivateBuildingDescription',
    [CompanyVatInfoSecondStepSelection.PrivateBuildingForDisabled]: 'vatInfo.privateBuildingForDisabledDescription'
}

function sendGtmEvent(formData: NewConnectionCmhGtmFormData) {  
  const eventBuilder = new EventBuilder(formData) 
  const event = eventBuilder.build()  
  gtmSender.send(event)    
}

class EventBuilder {
  
  formData: Readonly<NewConnectionCmhGtmFormData>

  constructor(formData: NewConnectionCmhGtmFormData) {
    this.formData = formData
  }

  getSiteTypeLevel() : string | null {
    return gtmHelper.format(process.env.VUE_APP_ENV as string)
  }

  getEventType(): string | null {    
    return this.formData.eventType.toString()
  }

  getFormName(): string | null {
    return 'nouveau_raccordement'
  }

  getLanguage(): string {    
    return this.formData.language
  }

  getFormStepName() : string {    
    return steps[this.formData.step]
  }

  getBuildingType(): string | null {   
    const addressPoint = this.formData.addressPoint

    if (this.formData.step > 0 && addressPoint) {      
      return buildingTypes[addressPoint.selection]
    }
    return null
  }

  getAppartmentCount(): string | null {            
    const addressPoint = this.formData.addressPoint
    
    if (this.formData.step > 0 && addressPoint && 
        addressPoint.selection === BuildingType.building &&
        addressPoint.flatNumber !== null && addressPoint.flatNumber !== undefined) {
      return addressPoint.flatNumber.toString()
    }
    return null
  }

  getCommonCounterCount(): string | null {    
    const addressPoint = this.formData.addressPoint

    if (this.formData.step > 0 && addressPoint && 
        addressPoint.selection === BuildingType.building &&  
        addressPoint.commonElectricMeterNumber !== null && addressPoint.commonElectricMeterNumber !== undefined) {      
      return addressPoint.commonElectricMeterNumber.toString()
    }    
    return null
  }

  getEnergyType(): string | null {
    const offeringSelections = this.formData.offeringSelections

    if (this.formData.step > 1 && offeringSelections) {
      const energyTypes: string[] = []

      if (offeringSelections.some(item => item === "electricity")) {
          energyTypes.push("electricite" )
      }

      if (offeringSelections.some(item => item === "gas")) {
        energyTypes.push("gaz" )
      }
     
      if (offeringSelections.some(item => item === "swde")) {
        energyTypes.push("eau" )
      }
      
      if (offeringSelections.some(item => item === "tvPhoneInternet")) {
        energyTypes.push("television,telephone_et_internet" )
      }
      
      if (energyTypes.length > 0) {
        return energyTypes.join("|")
      }
    }
    return null
  }

  getOperators(): string | null {  
    const offeringSelections = this.formData.offeringSelections

    if (this.formData.step > 1 && offeringSelections) {
      const operators: string[] = []

      if (offeringSelections.some(item => item === "proximusAndVoo")) {
        operators.push("proximus_et_voo" )
      }

      if (offeringSelections.some(item => item === "proximus")) {
        operators.push("proximus" )
      }

      if (offeringSelections.some(item => item === "voo")) {
        operators.push("voo" )
      }

      if (offeringSelections.some(item => item === "swde")) {
        operators.push("swde" )
      }

      if (operators.length > 0) {
        return operators.join("|")
      }
    }
    return null
  } 
  
  getPackagesName(packages?: PackagedElectricityMeter[] | null) : string[] {
    
    if (packages?.length) {    
      return packages
              .filter(p => !!p.package)
              .map(p => gtmHelper.format(p.package!.key)!)        
    }
    return []
  }
  
  getPackages(): string | null {    
    
    if (this.formData.step > 2 && this.formData.packages?.length) {                              
      const packages = this.getPackagesName(this.formData.packages)
      
      if (packages.length) {
        return packages.join('|')
      }
    }
    return null
  }

  getPackagesUpdateStatus(): string | null {
    
    if (this.formData.step > 2 && this.formData.packages?.length) {
      // Check if the user has modified its selection of packages.      
      const initialPackages = this.getPackagesName(this.formData.initialPackages).sort()
      const newPackages = this.getPackagesName(this.formData.packages).sort()      
      const areEqual = initialPackages.length === newPackages.length && 
        initialPackages.sort().every((value, index) => value === newPackages[index])

      return areEqual ? 'non' : 'oui'
    }
    return null
  }

  getUserProfile(): string | null {    
    const contactInfo = this.formData.contactInfo

    if (this.formData.step > 4 && contactInfo?.contactType) {      
      return userProfiles[contactInfo.contactType]
    }
    return null
  }
  
  getVat(): string | null {                
    
    if (this.formData.step > 5) {      
      let key = ''
      const individualVatInfo = this.formData.vatInfo?.individualVatInfoSelection
      const freelanceVatInfo = this.formData.vatInfo?.freelanceVatInfoSelection
      const companyVatInfo = this.formData.vatInfo?.companyVatInfoSelection

      // individual
      if (individualVatInfo !== null && individualVatInfo !== undefined) {
        key = individualVatInfoDescriptions[individualVatInfo]
        return gtmHelper.format(i18n.t(key, 'fr') as string)                
      }

      // freelance
      if (freelanceVatInfo !== null && freelanceVatInfo !== undefined) {
        key = freelanceVatInfoDescriptions[freelanceVatInfo]
        return gtmHelper.format(i18n.t(key, 'fr') as string)
      }

      // company
      if (companyVatInfo) {        
        const descriptions = []

        if (companyVatInfo.primaryInfo !== null && companyVatInfo.primaryInfo !== undefined) {
          key = companyPrimaryVatInfoDescriptions[companyVatInfo.primaryInfo]
          descriptions.push(gtmHelper.format(i18n.t(key, 'fr') as string))
        }

        if (companyVatInfo.secondaryInfo !== null && companyVatInfo.secondaryInfo !== undefined) {
          key = companySecondaryVatInfoDescriptions[companyVatInfo.secondaryInfo]                    
          descriptions.push(gtmHelper.format(i18n.t(key, 'fr') as string))
        }
        return descriptions.join("|")
      }    
    }        
    return null
  }

  getExtra(): string | null {
    
    if (this.formData.step > 6) {
      return this.formData.files?.length ? "oui" : "non"
    }    
    return null
  }

  getEstimatedCost(): string | null {
    
    if (this.formData.step > 7) {      
      const totalPrice = this.formData.totalPrice
      return totalPrice !== null && totalPrice !== undefined ? 
                gtmHelper.formatNumber(totalPrice) :
                "prix_a_calculer"            
    }    
    return null
  }

  getMaxElectricalPower(): string | null {
    
    if (this.formData.step > 7 && this.formData.packages?.length) {
      return this.formData.packages
              .filter(p => !!p.package)
              .map(p => gtmHelper.formatNumber(p.package!.maximumPower.kiloVoltAmpere)!.concat(' kVA') )
              .join('|')
    }
    return null
  }

  getCtaName(): string | null {
    return this.formData.ctaName ?? null
  }

  getLinkUrl(): string | null {
    return this.formData.linkUrl ?? null
  }

  public build(): NewConnectionCmhGtmEvent {    
    const event = new NewConnectionCmhGtmEvent()
   
    event.language = this.getLanguage()
    event.siteTypeLevel = this.getSiteTypeLevel()
    event.event = this.getEventType()
    event.form_name = this.getFormName()
    event.form_step = this.getFormStepName()
    
    switch(this.formData.eventType) {
      
      case GtmEventType.form_save:
      case GtmEventType.body_button_click: {
        event.cta_name = this.getCtaName()
        event.link_url = this.getLinkUrl()
        break
      }
      
      case GtmEventType.page_view: {
        event.building_type = this.getBuildingType()
        event.type_energy = this.getEnergyType()
        event.operators = this.getOperators()
        event.nb_appart = this.getAppartmentCount()
        event.nb_common_counter = this.getCommonCounterCount()
        event.forfait = this.getPackages()
        event.forfait_status = this.getPackagesUpdateStatus()
        event.user_profil = this.getUserProfile()
        event.tva_applicable = this.getVat()
        event.extra = this.getExtra()        
        event.estimated_cost = this.getEstimatedCost()
        event.puissance_electrique_max = this.getMaxElectricalPower()
        break
      }
    }    
    return event
  }
}

export const newConnectionCmhGtmService = {
  sendGtmEvent
}
